/**
 * Deal Metrics Calculator
 * 
 * Provides credible, explainable metrics for B2B sales:
 * - Closing Probability (based on stage, engagement, signals)
 * - Momentum (stable/slowing/at_risk)
 * - Expected Value (deal_value * probability)
 * - Probability Trend (qualitative: Up/Flat/Down)
 * 
 * All metrics are conservative and explainable.
 */

import { Lead } from '@/hooks/useLeads';
import { Deal } from '@/hooks/useDeals';
import { differenceInHours, isBefore, isAfter } from 'date-fns';

// ============================================================================
// TYPES
// ============================================================================

export interface CloseProbabilityResult {
  probability: number; // 0..1
  isEstimated: boolean;
  factors: string[];
}

export type MomentumStatus = 'stable' | 'slowing' | 'at_risk';

export interface MomentumResult {
  status: MomentumStatus;
  reasons: string[];
}

export type ProbabilityTrend = 'up' | 'flat' | 'down';

export interface ProbabilityTrendResult {
  trend: ProbabilityTrend;
  label: string; // e.g., "Decreasing", "Stable", "Improving"
  hasDelta: boolean; // true if we can show a percentage delta
  delta?: number; // percentage change (only if we have historical data)
}

// ============================================================================
// CLOSE PROBABILITY
// ============================================================================

/**
 * Compute closing probability for a deal based on:
 * - Stage (base probability)
 * - Last contact age
 * - Follow-up status
 * - SLA violations
 * - Engagement signals
 * 
 * Returns probability 0..1 and explanation factors.
 */
export function computeCloseProbability(
  deal: Deal,
  lead: Lead,
  now: Date = new Date()
): CloseProbabilityResult {
  const factors: string[] = [];
  let probability = 0;
  let isEstimated = true;

  // Base probability by stage
  const stageProbabilities: Record<string, number> = {
    'new': 0.20,
    'qualified': 0.35,
    'proposal': 0.50,
    'negotiation': 0.65,
    'won': 1.0,
    'lost': 0.0,
  };

  const baseProb = stageProbabilities[deal.stage] || stageProbabilities['new'];
  probability = baseProb;
  factors.push(`Stage: ${deal.stage} (${Math.round(baseProb * 100)}% base)`);

  // Last contact age modifier
  const lastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
  if (lastContact) {
    const hoursSinceContact = differenceInHours(now, lastContact);
    
    if (hoursSinceContact < 24) {
      probability += 0.05;
      factors.push('Recent contact (+5%)');
    } else if (hoursSinceContact >= 24 && hoursSinceContact < 72) {
      probability -= 0.05;
      factors.push(`Contact ${Math.floor(hoursSinceContact / 24)}d ago (-5%)`);
    } else if (hoursSinceContact >= 72) {
      probability -= 0.12;
      factors.push(`Contact ${Math.floor(hoursSinceContact / 24)}d ago (-12%)`);
    }
  } else {
    // Never contacted
    probability -= 0.10;
    factors.push('Never contacted (-10%)');
  }

  // Follow-up due or overdue
  const followUp = lead.next_action_at ? new Date(lead.next_action_at) : null;
  if (followUp && isBefore(followUp, now)) {
    probability -= 0.08;
    factors.push('Follow-up overdue (-8%)');
  } else if (!followUp && (deal.stage === 'qualified' || deal.stage === 'proposal' || deal.stage === 'negotiation')) {
    probability -= 0.05;
    factors.push('No follow-up scheduled (-5%)');
  }

  // SLA violation (hot=6h, warm=24h, cold=72h)
  const slaHours = lead.temperature === 'hot' ? 6 : lead.temperature === 'warm' ? 24 : 72;
  if (lastContact) {
    const hoursSinceContact = differenceInHours(now, lastContact);
    if (hoursSinceContact > slaHours) {
      probability -= 0.10;
      factors.push(`SLA violated (${lead.temperature} > ${slaHours}h) (-10%)`);
    }
  }

  // Recent reply signal (if we have it in meta or can infer from last_contact being very recent)
  // This is a placeholder - in real implementation, check for reply events
  if (lastContact && differenceInHours(now, lastContact) < 6) {
    // Likely a reply if contact was very recent
    probability += 0.08;
    factors.push('Recent engagement (+8%)');
  }

  // Clamp to reasonable bounds
  probability = Math.max(0.02, Math.min(0.95, probability));

  return {
    probability,
    isEstimated: true, // Always estimated unless we have historical close data
    factors,
  };
}

// ============================================================================
// MOMENTUM
// ============================================================================

/**
 * Compute momentum status for a deal:
 * - at_risk: SLA violated OR follow-up overdue OR last_contact > 72h
 * - slowing: last_contact 24-72h OR no follow-up scheduled OR stage stuck
 * - stable: otherwise
 */
export function computeMomentum(
  deal: Deal,
  lead: Lead,
  now: Date = new Date()
): MomentumResult {
  const reasons: string[] = [];
  let status: MomentumStatus = 'stable';

  const lastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
  const followUp = lead.next_action_at ? new Date(lead.next_action_at) : null;

  // Check for at_risk conditions
  if (lastContact) {
    const hoursSinceContact = differenceInHours(now, lastContact);
    
    // SLA violation
    const slaHours = lead.temperature === 'hot' ? 6 : lead.temperature === 'warm' ? 24 : 72;
    if (hoursSinceContact > slaHours) {
      status = 'at_risk';
      reasons.push(`SLA violated (${lead.temperature} > ${slaHours}h)`);
    }
    
    // Very stale contact
    if (hoursSinceContact > 72) {
      status = 'at_risk';
      reasons.push(`${Math.floor(hoursSinceContact / 24)}d since contact`);
    }
  } else {
    // Never contacted
    status = 'at_risk';
    reasons.push('Never contacted');
  }

  // Follow-up overdue
  if (followUp && isBefore(followUp, now)) {
    status = 'at_risk';
    reasons.push('Follow-up overdue');
  }

  // If not at_risk, check for slowing conditions
  if (status === 'stable') {
    if (lastContact) {
      const hoursSinceContact = differenceInHours(now, lastContact);
      
      // 24-72h since contact
      if (hoursSinceContact >= 24 && hoursSinceContact <= 72) {
        status = 'slowing';
        reasons.push(`${Math.floor(hoursSinceContact / 24)}d since contact`);
      }
    }

    // No follow-up scheduled for qualified+ stages
    if (!followUp && (deal.stage === 'qualified' || deal.stage === 'proposal' || deal.stage === 'negotiation')) {
      status = 'slowing';
      reasons.push('No follow-up scheduled');
    }

    // Stage stuck (no activity in 14+ days)
    if (deal.updated_at) {
      const daysSinceUpdate = differenceInHours(now, new Date(deal.updated_at)) / 24;
      if (daysSinceUpdate >= 14) {
        status = 'slowing';
        reasons.push(`No activity in ${Math.floor(daysSinceUpdate)}d`);
      }
    }
  }

  // If still stable, add positive reason
  if (status === 'stable' && reasons.length === 0) {
    reasons.push('On track');
  }

  return {
    status,
    reasons,
  };
}

// ============================================================================
// EXPECTED VALUE
// ============================================================================

/**
 * Compute expected value: deal_value * probability
 * Only returns value if deal.value is present.
 */
export function computeExpectedValue(
  deal: Deal,
  probability: number
): number | null {
  if (!deal.value || deal.value <= 0) {
    return null;
  }
  
  return deal.value * probability;
}

// ============================================================================
// PROBABILITY TREND
// ============================================================================

/**
 * Compute probability trend (qualitative).
 * Without historical data, we infer from:
 * - Last contact age increasing → Down
 * - SLA violation → Down
 * - Recent contact → Up/Flat
 * - No signals → Flat
 */
export function computeProbabilityDelta(
  deal: Deal,
  lead: Lead,
  now: Date = new Date()
): ProbabilityTrendResult {
  const lastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
  const followUp = lead.next_action_at ? new Date(lead.next_action_at) : null;

  // Without historical data, we can only infer trend from current state
  let trend: ProbabilityTrend = 'flat';
  let label = 'Stable';
  let hasDelta = false;

  // Down indicators
  if (lastContact) {
    const hoursSinceContact = differenceInHours(now, lastContact);
    
    // SLA violation → definitely down
    const slaHours = lead.temperature === 'hot' ? 6 : lead.temperature === 'warm' ? 24 : 72;
    if (hoursSinceContact > slaHours) {
      trend = 'down';
      label = 'Decreasing';
      hasDelta = false; // No specific percentage without historical data
    } else if (hoursSinceContact > 48) {
      // Stale contact → likely down
      trend = 'down';
      label = 'Decreasing';
      hasDelta = false;
    }
  } else {
    // Never contacted → down
    trend = 'down';
    label = 'Decreasing';
    hasDelta = false;
  }

  // Follow-up overdue → down
  if (followUp && isBefore(followUp, now)) {
    trend = 'down';
    label = 'Decreasing';
    hasDelta = false;
  }

  // Up indicators (recent contact)
  if (lastContact && differenceInHours(now, lastContact) < 24 && trend === 'flat') {
    trend = 'up';
    label = 'Improving';
    hasDelta = false;
  }

  return {
    trend,
    label,
    hasDelta,
  };
}

// ============================================================================
// HELPERS
// ============================================================================

export function formatProbability(probability: number): string {
  return `${Math.round(probability * 100)}%`;
}

export function formatExpectedValue(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

